import { useNavigate } from 'react-router-dom'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { Download } from '@factorialco/f0-react/icons/app'
import {
  BenefitsBenefitAvailabilityFragment,
  BenefitsCardBenefitListHealthcarePlanFragment,
  GetTrainingsSettingsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  BenefitsCardBenefit,
  type BenefitsCardBenefitActions,
} from 'modules/benefits/components/BenefitsCardBenefit/BenefitsCardBenefit'
import BenefitModals from 'modules/benefits/components/benefitModals/BenefitModals'
import { openModal } from 'modules/benefits/components/benefitModals/actions'
import { useBenefitModals } from 'modules/benefits/components/benefitModals/useBenefitModals'
import {
  ALL_SPENDING_LIMITED_BENEFIT_IDS,
  BenefitListing,
  BenefitSetupStatus,
  getBenefitsList,
  isSpendingLimitedBenefitCategory,
  SPENDING_LIMITED_BENEFIT_IDS,
} from 'modules/benefits/lib/benefitListings'

interface EmployeeBenefitsListProps {
  healthcarePlans: BenefitsCardBenefitListHealthcarePlanFragment[] | undefined
  healthcareEnrollmentStatus: BenefitSetupStatus | undefined
  featureAvailabilities: BenefitsBenefitAvailabilityFragment[] | undefined
  enableEnrollment: boolean
  /**
   * Spending limit balances for the employee.
   * - `undefined` means data is still loading — spending-limited benefits are hidden until loaded.
   * - `[]` (empty array) means loaded but no limits found — no benefits are hidden.
   */
  benefitBalances: Array<{ category: string; limitCents: number }> | undefined
}

const EmployeeBenefitsList = ({
  healthcarePlans,
  healthcareEnrollmentStatus: healthcareEnrollment,
  featureAvailabilities,
  enableEnrollment,
  benefitBalances,
}: EmployeeBenefitsListProps) => {
  const { employee, company } = useCurrentSession()
  const navigate = useNavigate()

  const { dispatch } = useBenefitModals()

  const [fetchTrainingsSettings, trainingsSettingsQuery] = useLazyQuery(
    GetTrainingsSettingsDocument,
    {
      fetchPolicy: 'cache-first',
      variables: { companyId: company.id },
    }
  )
  const trainingsSetupLoading = trainingsSettingsQuery.networkActivity

  const market = employee?.legalEntity?.country

  // While balance data is loading (undefined), hide all spending-limited benefits
  // to avoid a flash where they appear briefly before being filtered out.
  const hiddenBenefitIds =
    benefitBalances === undefined
      ? ALL_SPENDING_LIMITED_BENEFIT_IDS
      : new Set(
          benefitBalances
            .filter((b) => b.limitCents === 0)
            .flatMap((b) =>
              isSpendingLimitedBenefitCategory(b.category)
                ? SPENDING_LIMITED_BENEFIT_IDS[b.category]
                : []
            )
        )

  const allBenefits = market ? getBenefitsList(market, featureAvailabilities) : []
  const benefits = allBenefits
    .filter((benefit) => {
      if (benefit.availability !== 'active') {
        return false
      }
      if (hiddenBenefitIds.has(benefit.id)) {
        return false
      }
      if (benefit.id === 'health_insurance_es') {
        return healthcarePlans && healthcarePlans.length > 0
      }
      return true
    })
    .map((benefit) => {
      if (benefit.id !== 'health_insurance_es') {
        return benefit
      }
      return {
        ...benefit,
        // Hide the learn more URL from employees, the policy document is enough
        learnMoreUrl: undefined,
      }
    })

  /**
   * Determine what actions to show for a benefit.
   *
   * Right now we are generating this statically, but it will soon depend on
   * backend state.
   */
  function actionsForBenefit(benefit: BenefitListing): BenefitsCardBenefitActions | undefined {
    switch (benefit.id) {
      case 'childcare_es':
        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.request'),
            onClick: () => dispatch(openModal('childcare_request')),
          },
        }
      case 'health_insurance_es':
        if (benefit.availability !== 'active' || !healthcarePlans?.length) {
          return undefined
        }
        return {
          primaryAction: enableEnrollment
            ? {
                label: i18n.t('benefits.actions.enroll_now'),
                onClick: () => dispatch(openModal('healthcare_plan_enrollment')),
              }
            : undefined,
          secondaryAction: {
            label: i18n.t('benefits.actions.download_coverage'),
            icon: Download,
            onClick: () => {
              const url = healthcarePlans?.[0]?.planDocument?.url
              if (!url) return

              // Use a temporary anchor to open in a new tab with noopener/noreferrer
              // instead of window.open with features string (which may trigger popup behavior).
              const anchor = document.createElement('a')
              anchor.href = url
              anchor.target = '_blank'
              anchor.rel = 'noopener noreferrer'
              anchor.click()
            },
          },
        }

      case 'wellness_es':
        if (benefit.availability !== 'active') {
          return undefined
        }
        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.plan_details'),
            onClick: () => dispatch(openModal('wellness_information')),
          },
        }
      case 'trainings_es':
        if (benefit.availability !== 'active') {
          return undefined
        }

        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.request'),
            loading: trainingsSetupLoading,
            disabled: trainingsSetupLoading,
            onClick: async () => {
              try {
                const result = await fetchTrainingsSettings()
                if (result.error || !result.data) {
                  app.setFlash({
                    type: 'wrong',
                    message: i18n.t('errors.generic_error'),
                  })
                  return
                }

                const nodes = result.data.trainings.settingsConnection.nodes ?? []
                const enabled = nodes.some((n) => n.trainingRequestsEnabled === true)

                if (!enabled) {
                  app.setFlash({
                    type: 'wrong',
                    message: i18n.t('benefits.flash_messages.trainings_course_requests_disabled'),
                  })
                  return
                }

                dispatch(openModal('training_request'))
              } catch {
                app.setFlash({
                  type: 'wrong',
                  message: i18n.t('errors.generic_error'),
                })
              }
            },
          },
        }
      case 'perks_es':
        if (benefit.availability !== 'active') {
          return undefined
        }

        return {
          primaryAction: {
            label: i18n.t('benefits.actions.view_catalogue'),
            onClick: () => {
              // Navigate to perks catalogue sub-page
              navigate('/my-benefits/benefits/perks')
            },
          },
        }
    }
  }

  const setupStatus = (benefit: BenefitListing): BenefitSetupStatus | undefined => {
    if (benefit.id !== 'health_insurance_es') {
      return undefined
    }
    return healthcareEnrollment
  }

  return (
    <Box gap="s12" flexDirection="row" flexWrap="wrap">
      <Grid variant="fixed">
        {benefits.map((benefit) => {
          return (
            <Box
              key={benefit.id}
              gridColSpan={{ xxs: 12, xs: 12, s: 12, m: 4, l: 4, xl: 3, xxl: 3 }}
            >
              <BenefitsCardBenefit
                benefitListing={benefit}
                setupStatus={setupStatus(benefit)}
                showTag
                actions={actionsForBenefit(benefit)}
              />
            </Box>
          )
        })}
      </Grid>

      <BenefitModals />
    </Box>
  )
}

export { EmployeeBenefitsList }
